import { useState, useEffect, useCallback } from 'react';
import { Stack, Row, Text, Panel, Tag, Badge, Button, Input, EmptyState, MobileSheet } from '../ui';
import { api } from '../api/client';
import { useAuth } from '../api/useAuth';

type Tab = 'watchlist' | 'sources' | 'submissions';

const SEVERITY_TONE: Record<string, 'negative'|'warning'|'neutral'|'positive'|'accent'> = {
  red: 'negative', amber: 'warning', caution: 'neutral', green: 'positive',
};

const SOURCE_LABEL: Record<string, string> = {
  ofac_sdn: 'OFAC SDN',
  un_sc: 'UN Security Council',
  uk_hmt: 'UK HMT',
  eu_fsf: 'EU Consolidated',
  opensanctions_sanctions: 'OpenSanctions — Sanctions',
  opensanctions_maritime: 'OpenSanctions — Maritime',
  opensanctions_debarment: 'OpenSanctions — Debarment',
  opensanctions_crime: 'OpenSanctions — Crime & Fraud',
  opensanctions_peps: 'OpenSanctions — PEPs',
  group_list_legacy: 'Inner Circle members',
};

export function Trust() {
  const { friend } = useAuth();
  const isFounder = friend?.role === 'founder' || friend?.role === 'admin';
  const [tab, setTab] = useState<Tab>('watchlist');

  // Watchlist state
  const [entries, setEntries] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [q, setQ] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [selected, setSelected] = useState<any>(null);
  const [hitsForSelected, setHitsForSelected] = useState<any[]>([]);
  const PAGE_SIZE = 200;

  // Sources state
  const [sources, setSources] = useState<any[]>([]);
  const [syncBusy, setSyncBusy] = useState<string | null>(null);

  // Submissions state
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitForm, setSubmitForm] = useState<any>({ entityKind: 'company', proposedSeverity: 'red', displayName: '', reason: '' });

  const filterParams = useCallback(() => ({
    q: q || undefined,
    severity: severityFilter || undefined,
    source: sourceFilter || undefined,
    kind: kindFilter || undefined,
  }), [q, severityFilter, sourceFilter, kindFilter]);

  const loadWatchlist = useCallback(async () => {
    setLoading(true);
    setEntries([]);
    setHasMore(true);
    try {
      const [r, c] = await Promise.all([
        api.trustRegistry({ ...filterParams(), limit: PAGE_SIZE, offset: 0 }),
        api.trustRegistryCount(filterParams()),
      ]);
      setEntries(r.entries ?? []);
      setTotalCount(c.count ?? 0);
      setHasMore((r.entries?.length ?? 0) >= PAGE_SIZE);
    } catch {}
    setLoading(false);
  }, [filterParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const r = await api.trustRegistry({ ...filterParams(), limit: PAGE_SIZE, offset: entries.length });
      setEntries(prev => [...prev, ...(r.entries ?? [])]);
      setHasMore((r.entries?.length ?? 0) >= PAGE_SIZE);
    } catch {}
    setLoadingMore(false);
  }, [filterParams, loadingMore, hasMore, entries.length]);

  const loadSources = useCallback(async () => {
    try { const r = await api.trustSources(); setSources(r.sources ?? []); } catch {}
  }, []);

  const loadSubmissions = useCallback(async () => {
    try { const r = await api.trustSubmissions('pending'); setSubmissions(r.submissions ?? []); } catch {}
  }, []);

  useEffect(() => {
    if (tab === 'watchlist') loadWatchlist();
    if (tab === 'sources') loadSources();
    if (tab === 'submissions') loadSubmissions();
  }, [tab, loadWatchlist, loadSources, loadSubmissions]);

  const [sourcesForSelected, setSourcesForSelected] = useState<any[]>([]);
  const openEntry = async (entry: any) => {
    setSelected(entry);
    setHitsForSelected([]);
    setSourcesForSelected([]);
    try {
      const r: any = await api.trustEntry(entry.id);
      setHitsForSelected(r.hits ?? []);
      setSourcesForSelected(r.sources ?? []);
    } catch {}
  };

  const syncNow = async (sourceId?: string) => {
    setSyncBusy(sourceId ?? 'all');
    try {
      await api.trustSyncNow(sourceId ? [sourceId] : undefined);
      await loadSources();
    } catch (e: any) { alert('sync failed: ' + (e.message ?? 'unknown')); }
    setSyncBusy(null);
  };

  const submitProposal = async () => {
    if (!submitForm.displayName.trim() || submitForm.reason.length < 10) {
      alert('Name required and reason must be 10+ chars');
      return;
    }
    try {
      await api.trustSubmit(submitForm);
      setSubmitOpen(false);
      setSubmitForm({ entityKind: 'company', proposedSeverity: 'red', displayName: '', reason: '' });
      if (tab === 'submissions') loadSubmissions();
    } catch (e: any) { alert('submission failed: ' + (e.message ?? 'unknown')); }
  };

  const approveSubmission = async (id: string) => {
    try { await api.trustApproveSubmission(id); loadSubmissions(); }
    catch (e: any) { alert('approve failed: ' + (e.message ?? 'unknown')); }
  };

  return (
    <Stack gap={3}>
      <Row justify="between" align="center">
        <Stack gap={0.5}>
          <Text variant="display">Trust Center</Text>
          <Text muted variant="small">cross-references every entity against sanctions, blacklists, and member submissions</Text>
        </Stack>
        <Button variant="primary" size="sm" onClick={() => setSubmitOpen(true)}>+ flag entity</Button>
      </Row>

      <Row gap={0.5} className="overflow-x-auto no-scrollbar">
        {(['watchlist','sources','submissions'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`shrink-0 snap-start h-9 sm:h-7 px-3 sm:px-2 hairline rounded-sm text-xs transition-colors ${tab === t ? 'bg-ink text-bg' : 'text-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </Row>

      {tab === 'watchlist' && (
        <Stack gap={2}>
          <Row gap={2} wrap>
            <div className="w-full sm:w-72"><Input value={q} onChange={e => setQ(e.target.value)} placeholder="search by name, LEI, IMO…" /></div>
            <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)} className="h-9 hairline rounded-sm px-2 text-sm bg-bg">
              <option value="">all severity</option>
              <option value="red">red</option>
              <option value="amber">amber</option>
              <option value="caution">caution</option>
              <option value="green">green (whitelist)</option>
            </select>
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="h-9 hairline rounded-sm px-2 text-sm bg-bg">
              <option value="">all sources</option>
              {Object.entries(SOURCE_LABEL).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select value={kindFilter} onChange={e => setKindFilter(e.target.value)} className="h-9 hairline rounded-sm px-2 text-sm bg-bg">
              <option value="">all kinds</option>
              <option value="company">company</option>
              <option value="person">person</option>
              <option value="vessel">vessel</option>
            </select>
          </Row>
          <Panel title={`Showing ${entries.length.toLocaleString()} of ${totalCount.toLocaleString()}${loading ? ' (loading…)' : ''}`}>
            {entries.length === 0 ?
              <EmptyState title="no matches" hint="try a different search or run a sync from the Sources tab" /> :
              <Stack gap={0.5}>
                {entries.map((e: any) => (
                  <button key={e.id} onClick={() => openEntry(e)} className="text-left hairline-b py-2 hover:bg-muted/5 transition-colors">
                    <Row justify="between">
                      <Stack gap={0.5}>
                        <Row gap={1} wrap>
                          <Text variant="small" className="font-medium">{e.display_name}</Text>
                          <Badge tone={SEVERITY_TONE[e.severity] ?? 'neutral'}>{e.severity}</Badge>
                          <Tag>{e.entity_kind}</Tag>
                          {e.country && <Tag>{e.country}</Tag>}
                          {e.source_count > 1 && <Badge tone="warning">{e.source_count} sources</Badge>}
                        </Row>
                        <Row gap={2} wrap>
                          <Text variant="label">{e.source_count === 1 ? (SOURCE_LABEL[JSON.parse(e.source_list ?? '[]')[0]] ?? 'source') : `${e.source_count} sources flag this entity`}</Text>
                          {e.hit_count > 0 && <Text variant="label" className="text-negative">{e.hit_count} hit{e.hit_count > 1 ? 's' : ''} on your deals</Text>}
                        </Row>
                      </Stack>
                    </Row>
                  </button>
                ))}
              </Stack>
            }
            {hasMore && entries.length > 0 && (
              <Row className="mt-3" justify="center">
                <Button variant="secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'loading…' : `load more (${(totalCount - entries.length).toLocaleString()} remaining)`}
                </Button>
              </Row>
            )}
          </Panel>
        </Stack>
      )}

      {tab === 'sources' && (
        <Panel title={`${sources.length} configured sources`}>
          <Stack gap={1}>
            {sources.map((s: any) => {
              const last = s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : 'never';
              const status = s.last_sync_status ?? 'unknown';
              const tone = status === 'ok' ? 'positive' : status === 'error' ? 'negative' : 'neutral';
              return (
                <div key={s.id} className="hairline-b py-2">
                  <Row justify="between" align="center">
                    <Stack gap={0.5}>
                      <Row gap={1} wrap>
                        <Text variant="small" className="font-medium">{s.display_name}</Text>
                        <Badge tone={tone}>{status}</Badge>
                        {s.enabled ? <Tag>enabled</Tag> : <Tag>disabled</Tag>}
                      </Row>
                      <Row gap={2} wrap>
                        <Text variant="label">{(s.rows_active ?? 0).toLocaleString()} active rows</Text>
                        <Text variant="label">last sync: {last}</Text>
                      </Row>
                      {s.last_sync_error && <Text variant="label" className="text-negative break-all">{s.last_sync_error}</Text>}
                    </Stack>
                    {isFounder && (
                      <Button variant="secondary" size="sm" onClick={() => syncNow(s.id)} disabled={syncBusy === s.id || syncBusy === 'all'}>
                        {syncBusy === s.id ? 'syncing…' : 'sync now'}
                      </Button>
                    )}
                  </Row>
                </div>
              );
            })}
          </Stack>
          {isFounder && (
            <Row className="mt-3" gap={1}>
              <Button variant="primary" onClick={() => syncNow()} disabled={!!syncBusy}>
                {syncBusy === 'all' ? 'syncing all…' : 'sync all sources now'}
              </Button>
              <Text variant="label" className="self-center">automatic daily sync at 02:00 UTC</Text>
            </Row>
          )}
        </Panel>
      )}

      {tab === 'submissions' && (
        <Panel title={`${submissions.length} pending submissions`}>
          {submissions.length === 0 ?
            <EmptyState title="no pending submissions" hint="member-proposed flags appear here for two-member approval" /> :
            <Stack gap={1}>
              {submissions.map((s: any) => {
                const approvals = JSON.parse(s.approvals ?? '[]');
                const alreadyApproved = friend ? approvals.includes(friend.id) : false;
                const isProposer = friend?.id === s.proposed_by;
                return (
                  <div key={s.id} className="hairline-b py-2">
                    <Row justify="between">
                      <Stack gap={0.5}>
                        <Row gap={1} wrap>
                          <Text variant="small" className="font-medium">{s.display_name}</Text>
                          <Badge tone={SEVERITY_TONE[s.proposed_severity] ?? 'neutral'}>{s.proposed_severity}</Badge>
                          <Tag>{s.entity_kind}</Tag>
                        </Row>
                        <Text variant="label">proposed by {s.proposed_by_name ?? 'someone'}</Text>
                        <Text variant="small">{s.reason}</Text>
                        <Text variant="label">{approvals.length} approval{approvals.length === 1 ? '' : 's'}</Text>
                      </Stack>
                      {!isProposer && !alreadyApproved && (
                        <Button variant="primary" size="sm" onClick={() => approveSubmission(s.id)}>approve</Button>
                      )}
                    </Row>
                  </div>
                );
              })}
            </Stack>
          }
        </Panel>
      )}

      {/* Detail sheet for selected registry entry */}
      {selected && (
        <MobileSheet open={!!selected} onClose={() => setSelected(null)} title={selected.display_name}>
          <Stack gap={2}>
            <Row gap={1} wrap>
              <Badge tone={SEVERITY_TONE[selected.severity] ?? 'neutral'}>{selected.severity}</Badge>
              <Tag>{selected.entity_kind}</Tag>
              {selected.country && <Tag>{selected.country}</Tag>}
            </Row>

            {sourcesForSelected.length > 0 && (
              <Stack gap={1}>
                <Text variant="small" className="font-medium">Flagged on {sourcesForSelected.length} source{sourcesForSelected.length === 1 ? '' : 's'}</Text>
                <Stack gap={0.5}>
                  {sourcesForSelected.map((src: any) => (
                    <div key={`${src.source}-${src.source_id}`} className="hairline rounded-sm p-2">
                      <Row justify="between" wrap>
                        <Stack gap={0.5} className="flex-1 min-w-0">
                          <Row gap={1} wrap>
                            <Text variant="small" className="font-medium">{SOURCE_LABEL[src.source] ?? src.source}</Text>
                            <Badge tone={SEVERITY_TONE[src.severity] ?? 'neutral'}>{src.severity}</Badge>
                          </Row>
                          {src.reason && <Text variant="label" className="break-words">{src.reason}</Text>}
                          <Text variant="label">last synced: {new Date(src.last_synced_at).toLocaleDateString()}</Text>
                        </Stack>
                        {src.source_url && (
                          <a href={src.source_url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline shrink-0 self-center">
                            view →
                          </a>
                        )}
                      </Row>
                    </div>
                  ))}
                </Stack>
              </Stack>
            )}
            <div>
              <Text variant="small" className="font-medium mb-1">Hits across your deals ({hitsForSelected.length})</Text>
              {hitsForSelected.length === 0 ? <Text variant="label" muted>no hits — this entity has not appeared on any of your deals</Text> :
                <Stack gap={0.5}>
                  {hitsForSelected.map((h: any) => (
                    <div key={h.id} className="hairline rounded-sm p-2">
                      <Row justify="between" wrap>
                        <Text variant="small">{h.subject_name ?? h.hit_type} ({h.matched_on}, {(h.match_confidence*100).toFixed(0)}%)</Text>
                        <Text variant="label">{new Date(h.created_at).toLocaleString()}</Text>
                      </Row>
                    </div>
                  ))}
                </Stack>
              }
            </div>
          </Stack>
        </MobileSheet>
      )}

      {/* Flag-an-entity submission */}
      {submitOpen && (
        <MobileSheet open={submitOpen} onClose={() => setSubmitOpen(false)} title="Flag an entity">
          <Stack gap={2}>
            <Text variant="small" muted>Proposed flags need a second member's approval before going live in the registry.</Text>
            <Stack gap={1}>
              <Text variant="label">Entity kind</Text>
              <select value={submitForm.entityKind} onChange={e => setSubmitForm({ ...submitForm, entityKind: e.target.value })}
                className="h-9 hairline rounded-sm px-2 text-sm bg-bg">
                <option value="company">company</option>
                <option value="person">person</option>
                <option value="vessel">vessel</option>
                <option value="wallet">wallet</option>
              </select>
            </Stack>
            <Stack gap={1}>
              <Text variant="label">Display name</Text>
              <Input value={submitForm.displayName} onChange={e => setSubmitForm({ ...submitForm, displayName: e.target.value })}
                placeholder="e.g. Vortex Trading Ltd" />
            </Stack>
            <Stack gap={1}>
              <Text variant="label">Severity</Text>
              <select value={submitForm.proposedSeverity} onChange={e => setSubmitForm({ ...submitForm, proposedSeverity: e.target.value })}
                className="h-9 hairline rounded-sm px-2 text-sm bg-bg">
                <option value="red">red — blacklist</option>
                <option value="caution">caution — historical concern</option>
                <option value="green">green — whitelist (vetted)</option>
              </select>
            </Stack>
            <Stack gap={1}>
              <Text variant="label">Reason (10+ chars, audited)</Text>
              <Input value={submitForm.reason} onChange={e => setSubmitForm({ ...submitForm, reason: e.target.value })}
                placeholder="why is this entity flagged?" />
            </Stack>
            <Row gap={1}>
              <Button variant="primary" onClick={submitProposal}>submit for approval</Button>
              <Button variant="secondary" onClick={() => setSubmitOpen(false)}>cancel</Button>
            </Row>
          </Stack>
        </MobileSheet>
      )}
    </Stack>
  );
}
